/* Zones kiosk: 1024x768 landscape, readable from three metres, no chrome, nowhere else to go.
   Opens on /z/<token>. Only the staff controls and the volume can be touched; everything else is display. */
import { Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { Annunciators, Faceplate, Glass, Key, Knob, Lamp, Readout, Spectrum, Sym, VuMeter, hzAt } from '../../hifi';
import { WEBAUDIO_METERS, createStreamPlayer } from '../../audio/player';
import { envelopeLevels, startMeters, webAudioLevels } from '../../audio/meters';
import { API, api, ApiError, Branding, NowPlaying, loadDevice, saveDevice } from '../../api';
import Pairing from './Pairing';

const fmt = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const upper = (s: string) => s.toUpperCase();

export default function Kiosk(props: { token: string }) {
  const [branding, setBranding] = createSignal<Branding | null>(null);
  const [now, setNow] = createSignal<NowPlaying | null>(null);
  const [revoked, setRevoked] = createSignal(false);
  const [net, setNet] = createSignal(navigator.onLine);
  const [clock, setClock] = createSignal(new Date());
  const [volume, setVolume] = createSignal(60);
  const [busy, setBusy] = createSignal(false);
  const [device, setDevice] = createSignal(loadDevice());
  const player = createStreamPlayer();
  let meters: ReturnType<typeof startMeters> | null = null;
  const needles: SVGLineElement[] = [];
  const vuSvgs: SVGSVGElement[] = [];
  let columns: HTMLDivElement[] = [];
  const timers: number[] = [];

  const ceiling = () => branding()?.zone.volume_ceiling ?? 100;
  const myDevice = () => (device() && device()!.zone_id === branding()?.zone.id ? device() : null);

  async function loadBranding() {
    try {
      const b = await api<Branding>('/zone/branding', { token: props.token });
      setBranding(b);
      setRevoked(false);
      setNet(true);
      if (volume() > b.zone.volume_ceiling) setVolume(b.zone.volume_ceiling);
      player.setUrls(b.hls_url, b.icecast_url);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { setRevoked(true); player.stop(); }
      else setNet(false);
      return false;
    }
  }

  async function poll() {
    try {
      // Ask for what is audible: the HLS program date time, which trails the wall clock by the buffer.
      const at = player.playingDate();
      const q = at ? `?at=${encodeURIComponent(at.toISOString())}` : '';
      setNow(await api<NowPlaying>('/zone/now' + q, { token: props.token }));
      setNet(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { setRevoked(true); player.stop(); }
      else setNet(false);
    }
  }

  async function heartbeat() {
    const d = myDevice();
    if (!d) return;
    try {
      const r = await api<{ commands: { command: string; args: any }[] }>('/device/heartbeat', {
        method: 'POST', deviceKey: d.device_key,
        body: JSON.stringify({ playing: player.state() === 'playing', volume: volume(), version: 'web-0.1.0',
          buffering: player.state() === 'buffering', track_id: now()?.now?.track?.id ?? null }),
      });
      for (const c of r.commands) {
        if (c.command === 'restart') location.reload();
        if (c.command === 'set_volume' && typeof c.args?.volume === 'number') applyVolume(c.args.volume);
        if (c.command === 'refresh_token' && c.args?.token) {
          if (c.args.zone_id) saveDevice({ ...d, zone_id: c.args.zone_id }); // moved to another zone
          location.replace(`/z/${c.args.token}`);
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { saveDevice(null); setDevice(null); }
    }
  }

  function applyVolume(v: number) {
    setVolume(Math.min(v, ceiling()));  // the element follows (see the pause effect below)
  }

  // Once the meters tap the element, all sound goes through their AudioContext. A context created without a user
  // gesture stays suspended: the element "plays" in silence and nothing errors. Treat that as blocked.
  const [audioLocked, setAudioLocked] = createSignal(false);
  const unlock = () => { void meters?.resume(); if (player.state() === 'blocked') player.resume(); };
  for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) document.addEventListener(ev, unlock, { capture: true });
  onCleanup(() => { for (const ev of ['pointerdown', 'keydown', 'touchend'] as const) document.removeEventListener(ev, unlock, { capture: true }); });

  function startAudio() {
    if (!meters) {
      const targets = () => ({ needles, vuSvgs, columns });
      try {
        // Safari: its Web Audio gets only zeros from the stream, so the meters follow the per-track
        // envelopes instead and the audio stays on Safari's own HLS player.
        meters = startMeters(WEBAUDIO_METERS ? webAudioLevels(player.audio, 12) : envelopeLevels({
          url: (id) => `${API}/zone/levels/${id}?zt=${encodeURIComponent(props.token)}`, audibleMs: player.audibleMs, bands: 12,
          items: () => [now()?.now ?? null, now()?.next ?? null].map((i) => i && { key: i.track?.id ?? null, started_at: i.started_at, duration_ms: i.duration_ms }),
        }), targets, 12);
        const ctx = meters.ctx;
        if (ctx) {
          setAudioLocked(ctx.state !== 'running');
          ctx.addEventListener('statechange', () => setAudioLocked(ctx.state !== 'running'));
        }
      } catch { /* no Web Audio: meters rest */ }
    }
    void meters?.resume();
    player.start();
  }

  async function control(action: 'pause' | 'resume' | 'skip') {
    const d = myDevice();
    if (!d || busy()) return;
    setBusy(true);
    try {
      await api('/device/control', { method: 'POST', deviceKey: d.device_key, body: JSON.stringify({ action }) });
      await poll();
    } finally { setBusy(false); }
  }

  onMount(async () => {
    const ok = await loadBranding();
    await poll();
    if (ok) startAudio();
    timers.push(window.setInterval(() => setClock(new Date()), 1000));
    timers.push(window.setInterval(poll, 5000));
    timers.push(window.setInterval(heartbeat, 30000));
    timers.push(window.setInterval(() => { if (revoked() || !branding()) void loadBranding(); }, 15000));
    void heartbeat();
    const on = () => { setNet(true); if (player.state() === 'offline') player.start(); };
    const off = () => setNet(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    onCleanup(() => { window.removeEventListener('online', on); window.removeEventListener('offline', off); });
  });
  onCleanup(() => { timers.forEach(clearInterval); player.stop(); meters?.stop(); });

  const status = () => now()?.zone.status ?? 'active';
  // A pause reaches the stream only after the publish lead and this player's buffer (half a minute or so). Go quiet
  // here as soon as the zone is paused; on resume the stream's silence plays out until the music comes back.
  createEffect(() => player.setVolume(status() === 'paused' ? 0 : volume() / 100));
  const playing = () => status() === 'active' && player.state() === 'playing';
  const offline = () => !net() || player.state() === 'offline';
  const words = createMemo(() => [
    { text: 'PLAYING', on: playing() },
    { text: 'PAUSED', on: status() === 'paused' },
    { text: 'SCHEDULE', on: status() === 'active' && !!now()?.schedule && !now()!.schedule.always_open },
    { text: 'MANUAL', on: false },
    { text: 'OFFLINE', on: offline(), red: true },
  ]);

  const audible = () => player.playingDate()?.getTime() ?? (now() ? Date.parse(now()!.server_time) : Date.now());
  const elapsed = () => {
    const n = now()?.now;
    return n ? audible() - Date.parse(n.started_at) : 0;
  };
  const trackLine = () => {
    const n = now();
    if (!n) return 'CONNECTING';
    if (player.state() === 'blocked' || (audioLocked() && player.state() === 'playing')) return 'TOUCH ▶ TO START';
    if (n.suspended) return 'SERVICE SUSPENDED';
    if (n.zone.status === 'paused') return n.now?.track ? upper(`${n.now.track.title} — ${n.now.track.artist}`) : 'PAUSED';
    if (closed() && n.now?.kind !== 'track') return 'CLOSED';
    if (!n.now || n.now.kind === 'silence') return 'NO PLAYLIST';
    return n.now.track ? upper(`${n.now.track.title} — ${n.now.track.artist}`) : upper(n.now.kind);
  };
  const closed = () => now()?.schedule?.open === false;
  const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const nextLine = () => {
    const n = now();
    if (closed() && n?.schedule.next_change_at) {
      const at = new Date(n.schedule.next_change_at);
      const sameDay = at.toDateString() === new Date().toDateString();
      return `OPENS ${sameDay ? '' : upper(at.toLocaleDateString([], { weekday: 'short' })) + ' '}${hhmm(n.schedule.next_change_at)}`;
    }
    const t = n?.next?.track;
    return t ? `NEXT ${upper(t.title)}` : '';
  };
  const hoursLine = () => {
    const sc = now()?.schedule;
    if (!sc || sc.always_open) return 'OPEN 24H';
    if (!sc.hours_today.length) return 'CLOSED TODAY';
    // back-to-back slots (a daypart change) read as one stretch: 09:00-18:00 + 18:00-23:00 -> 09:00–23:00
    const spans: [string, string][] = [];
    for (const h of sc.hours_today) {
      const [a, b] = h.split('-');
      const last = spans[spans.length - 1];
      if (last && last[1] === a) last[1] = b; else spans.push([a, b]);
    }
    return 'TODAY ' + spans.map(([a, b]) => (a === b ? '24H' : `${a}–${b}`)).join(' · ');
  };
  const sourceLine = () => {
    const n = now();
    if (!n) return '';
    if (n.follows) return `FOLLOWS ${upper(n.follows.name)}`;
    const dp = n.schedule?.daypart;
    if (n.zone.playlist_kind === 'auto') return dp && dp !== 'default' ? `${upper(dp.replace(/-/g, ' '))} MIX` : 'GENRE MIX';
    return n.zone.playlist_kind === 'manual' ? 'HAND-BUILT PLAYLIST' : '';
  };
  const lampStream = () => (player.state() === 'playing' && !audioLocked() ? 'green' : player.state() === 'buffering' || player.state() === 'starting' ? 'warn' : 'off');
  const lampSync = () => {
    const d = player.playingDate();
    return d && Math.abs(Date.now() - d.getTime()) < 60_000 && status() === 'active' ? 'green' : 'off';
  };

  return (
    <Show when={!revoked()} fallback={<Pairing onPaired={(d, token) => { saveDevice(d); setDevice(d); location.replace(`/z/${token}`); }} />}>
      <div class="mx-auto flex h-screen max-h-[768px] w-full max-w-[1024px] flex-col gap-[14px] p-5">
        <Faceplate as="header" class="flex items-center gap-6 px-5 py-3">
          <div class="flex items-center gap-2.5">
            <Show when={branding()?.branding.logo} fallback={<>
              <img src="/mark.png" alt="" class="h-7 w-10 object-contain" />
              <span class="wordmark text-xl font-semibold">scai.fm</span>
              <span class="wordmark text-xl" style={{ color: 'var(--vfd)' }}>Zones</span>
            </>}>
              <img src={branding()!.branding.logo!} alt={branding()!.branding.title} class="h-8 max-w-[200px] object-contain" />
            </Show>
          </div>
          <div class="ml-auto flex items-center gap-[18px]">
            <Lamp tone={net() ? 'green' : 'off'} word="Net" />
            <Lamp tone={lampStream()} word="Stream" />
            <Lamp tone={lampSync()} word="Sync" />
            <Lamp tone={now()?.now?.kind === 'announcement' ? 'warn' : 'off'} word="Announce" />
            <Glass class="rounded px-2.5 py-1 text-lg vfd">
              {clock().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
            </Glass>
          </div>
        </Faceplate>

        <Faceplate as="main" class="grid min-h-0 flex-grow grid-cols-12 grid-rows-[minmax(0,1fr)_auto] gap-4 p-5">
          <Glass class="col-span-8 flex min-w-0 flex-col gap-3.5 px-7 py-6">
            <Annunciators words={words()} />
            <Readout size="m" tone="muted" text={upper(`${branding()?.customer.name ?? ''} · ${branding()?.zone.name ?? ''}`)} />
            <Readout size="xl" text={upper(branding()?.zone.name ?? '—')} />
            <Readout size="l" text={trackLine()} live />
            <Readout size="m" tone="muted" text={sourceLine()} />
            <div class="mt-auto flex justify-between gap-4 text-[15px] tracking-[0.08em]">
              <Show when={now()?.now?.track}>
                <span class="vfd whitespace-nowrap">{fmt(elapsed())} / {fmt(now()?.now?.duration_ms ?? 0)}</span>
              </Show>
              <span class="vfd-muted truncate">{nextLine()}</span>
              <span class="vfd-muted whitespace-nowrap">{hoursLine()}</span>
            </div>
          </Glass>

          <div class="col-span-4 flex flex-col gap-3">
            <div class="grid grid-cols-2 gap-2.5">
              <Glass class="p-2"><VuMeter channel="L" needleRef={(el) => (needles[0] = el)} labelRef={(el) => (vuSvgs[0] = el)} /></Glass>
              <Glass class="p-2"><VuMeter channel="R" needleRef={(el) => (needles[1] = el)} labelRef={(el) => (vuSvgs[1] = el)} /></Glass>
            </div>
            <Glass class="flex-grow px-3.5 pb-2 pt-3" role="img" aria-label="Spectrum analyser, twelve bands">
              <Spectrum columns={12} colRef={(els) => (columns = els)}
                scale={[{ label: '60', at: 0 }, { label: '250', at: hzAt(250) }, { label: '1k', at: hzAt(1000) }, { label: '4k', at: hzAt(4000) }, { label: '16k Hz', at: 1 }]} />
            </Glass>
          </div>

          <div class="col-span-12 flex items-end gap-8 border-t border-hairline pt-3.5">
            <div class="flex flex-col gap-2">
              <span class="legend">Staff controls</span>
              <div class="flex gap-3">
                <Show when={player.state() === 'blocked' || player.state() === 'idle' || audioLocked()}>
                  <Key size="kiosk" symbol={<Sym name="play" />} aria-label="Start playback" primary onClick={() => { void meters?.resume(); player.state() === 'blocked' ? player.resume() : startAudio(); }} />
                </Show>
                <Show when={status() === 'paused'} fallback={
                  <Key size="kiosk" symbol={<Sym name="pause" />} aria-label="Pause zone" disabled={!myDevice() || busy()} onClick={() => control('pause')} />}>
                  <Key size="kiosk" symbol={<Sym name="play" />} aria-label="Resume zone" latched disabled={!myDevice() || busy()} onClick={() => control('resume')} />
                </Show>
                <Key size="kiosk" symbol={<Sym name="skip" />} aria-label="Skip track" disabled={!myDevice() || busy() || status() !== 'active'} onClick={() => control('skip')} />
              </div>
            </div>
            <div class="ml-auto flex items-end gap-6">
              <div class="flex flex-col gap-1 text-right font-mono text-xs" style={{ color: 'var(--legend-muted)' }}>
                <span>play.scai.fm/z/{props.token.slice(0, 10)}…<Show when={player.source()}> · {({ hlsjs: 'HLS', native: 'HLS (native)', mp3: 'MP3' } as const)[player.source()!]}</Show></span>
                <span>{myDevice() ? `device ${myDevice()!.name} · paired` : 'not paired · staff controls off · pair at scai.fm/pair'}</span>
              </div>
              <Knob label={`Volume · max ${ceiling()}`} value={volume()} max={ceiling()} onInput={applyVolume} />
            </div>
          </div>
        </Faceplate>
      </div>
    </Show>
  );
}
